import { useState } from 'react'
import toast from 'react-hot-toast'
import Modal from '../../ui/Modal'
import { deliveriesAPI } from '../../../api/deliveries'

const CANCEL_REASONS = [
  'Erreur de saisie (mauvaise adresse)',
  'Commande annulée par le client final',
  'Article indisponible',
  'Délai de livraison trop long',
  'Changement de prestataire',
  'Autre',
]

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 13,
  fontWeight: 600, color: '#374151', marginBottom: 8,
}

const textareaStyle: React.CSSProperties = {
  width: '100%', padding: '12px 14px',
  borderRadius: 12, border: '1.5px solid #E5E7EB',
  fontSize: 14, fontFamily: "'DM Sans', sans-serif",
  resize: 'vertical', outline: 'none',
  background: '#FAFAFA', color: '#0D1117',
  boxSizing: 'border-box',
}

const btnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  border: 'none', borderRadius: 12,
  padding: '12px 20px', color: '#fff',
  fontSize: 14, fontWeight: 600,
  cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  flex: 1, justifyContent: 'center',
}

interface Props {
  deliveryId: string
  trackingCode: string
  onClose: () => void
  onSuccess: () => void
}

export const ExpCancelModal = ({ deliveryId, trackingCode, onClose, onSuccess }: Props) => {
  const [reason, setReason] = useState('')
  const [comment, setComment] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const handleCancel = async () => {
    if (!reason) { setError('Veuillez sélectionner un motif'); return }
    if (reason === 'Autre' && !comment.trim()) { setError('Veuillez préciser le motif'); return }

    setIsLoading(true)
    setError('')
    try {
      await deliveriesAPI.updateStatus(deliveryId, {
        status: 'cancelled',
        reason,
        comment: comment.trim() || reason,
      })
      toast.success('Colis annulé avec succès')
      onSuccess()
      onClose()
    } catch {
      setError("Erreur lors de l'annulation")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal open title={`Annuler le colis ${trackingCode}`} onClose={onClose} size="sm">
      <div style={{
        background: '#FEF2F2', border: '1px solid #FECACA',
        borderRadius: 10, padding: '12px 16px', marginBottom: 20,
        display: 'flex', gap: 10, alignItems: 'flex-start',
      }}>
        <span style={{ fontSize: 18 }}>⚠️</span>
        <div style={{ fontSize: 13, color: '#991B1B' }}>
          <strong>Action irréversible.</strong> Une fois annulé, le colis ne peut plus être
          réactivé. Un nouveau colis devra être créé si nécessaire.
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Motif d'annulation *</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {CANCEL_REASONS.map((r) => (
            <label key={r} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 14px', borderRadius: 10,
              border: `1px solid ${reason === r ? '#EF4444' : '#E5E7EB'}`,
              background: reason === r ? '#FEF2F2' : '#fff',
              cursor: 'pointer', fontSize: 14,
            }}>
              <input
                type="radio" name="expCancelReason" value={r}
                checked={reason === r}
                onChange={() => { setReason(r); setError('') }}
                style={{ accentColor: '#EF4444' }}
              />
              {r}
            </label>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>
          Commentaire {reason === 'Autre' ? '*' : '(optionnel)'}
        </label>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Informations complémentaires..."
          rows={3} maxLength={500}
          style={textareaStyle}
        />
        <div style={{ textAlign: 'right', fontSize: 11, color: '#9CA3AF' }}>
          {comment.length}/500
        </div>
      </div>

      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '10px 14px', color: '#EF4444', fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={onClose} style={{ ...btnStyle, background: '#F3F4F6', color: '#374151' }}>
          Retour
        </button>
        <button
          onClick={handleCancel}
          disabled={isLoading}
          style={{ ...btnStyle, background: '#EF4444', opacity: isLoading ? 0.7 : 1, cursor: isLoading ? 'not-allowed' : 'pointer' }}
        >
          {isLoading ? 'Annulation...' : "Confirmer l'annulation"}
        </button>
      </div>
    </Modal>
  )
}
